import { useRecaptcha } from '@/contexts/RecaptchaContext';
import { useState, useCallback } from 'react';
import { buildApiUrl } from '@/config/env';
import { createApiHeaders } from './user';

// Define the API endpoints that require reCAPTCHA
const RECAPTCHA_ENDPOINTS = [
  '/api/s3/presigned-urls',
  '/api/video/complete-upload',
  '/api/video/start-full-processing',
  '/api/payment/create-order',
  '/api/process-full-video',
  'https://b5wnyvepomkadu55blus4q47pq0nehih.lambda-url.eu-central-1.on.aws',
  'https://xyz2db21w8.execute-api.eu-central-1.amazonaws.com/sub/translate-vtt'  // 🚀 NEW: VTT translation endpoint
];

// Helper function to check if an endpoint requires reCAPTCHA
export const requiresRecaptcha = (endpoint: string): boolean => {
  return RECAPTCHA_ENDPOINTS.some(recaptchaEndpoint => 
    endpoint.includes(recaptchaEndpoint)
  );
};

// Helper function to get the appropriate action for an endpoint
export const getRecaptchaAction = (endpoint: string): string => {
  if (endpoint.includes('/api/s3/')) return 'file_upload';
  if (endpoint.includes('/api/video/')) return 'video_processing';
  if (endpoint.includes('/api/payment/')) return 'payment';
  if (endpoint.includes('/api/process-full-video')) return 'full_video_processing';
  if (endpoint.includes('translate-srt') || endpoint.includes('translate-vtt')) return 'translation';
  return 'general';
};

// Base URL for Cloudflare Worker
const getCFWorkerUrl = (): string => {
  return buildApiUrl('');
};

// 🚀 Helper function to clean duplicate https in URLs
const cleanUrl = (url: string): string => {
  // Remove duplicate https if exists (e.g., https://domain.com/https://other.com -> https://other.com)
  return url.replace(/^https:\/\/[^\/]+\/https:\/\//, 'https://');
};

// Retry mechanism for reCAPTCHA race conditions
const retryWithDelay = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      console.log(`🔄 reCAPTCHA attempt ${attempt} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 1.5; // Exponential backoff
    }
  }
  
  throw lastError!;
};

// API wrapper function that adds reCAPTCHA token with retry
export const apiCallWithRecaptcha = async (
  endpoint: string,
  options: RequestInit = {},
  recaptchaToken?: string
): Promise<Response> => {
  const baseUrl = getCFWorkerUrl();
  
  // 🚀 FIX: Prevent double slashes by cleaning both baseUrl and endpoint
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const cleanEndpoint = endpoint.replace(/^\//, '');
  const fullUrl = `${cleanBaseUrl}/${cleanEndpoint}`;
  
  console.log('🔍 apiCallWithRecaptcha - URL construction debug:', {
    originalBaseUrl: baseUrl,
    originalEndpoint: endpoint,
    cleanBaseUrl,
    cleanEndpoint,
    fullUrl,
    hasDoubleSlash: fullUrl.includes('//api')
  });
  
  // If reCAPTCHA is required and no token provided, throw error
  if (requiresRecaptcha(endpoint) && !recaptchaToken) {
    throw new Error('reCAPTCHA token required for this endpoint');
  }

  // Add reCAPTCHA token to headers if provided
  const headers: Record<string, string> = {
    ...createApiHeaders(),
    ...(options.headers as Record<string, string>),
  };

  if (recaptchaToken) {
    headers['X-Recaptcha-Token'] = recaptchaToken;
  }

  return fetch(cleanUrl(fullUrl), {
    ...options,
    headers,
  });
};

// 🚀 API utility with reCAPTCHA integration
export const useApiWithRecaptcha = () => {
  const { executeRecaptcha, isLoaded, isLoading: recaptchaLoading, error: recaptchaError, lastTokenTime } = useRecaptcha();
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const callApiWithRecaptcha = useCallback(async (
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> => {
    setApiLoading(true);
    setApiError(null);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('api_call');
      
      // Add reCAPTCHA token to headers
      const headers = {
        ...createApiHeaders(),
        'X-Recaptcha-Token': recaptchaToken,
        ...options.headers,
      };

      // Build full URL
      const fullUrl = endpoint.startsWith('http') ? endpoint : buildApiUrl(endpoint);

      // Make API call
      const response = await fetch(cleanUrl(fullUrl), {
        ...options,
        headers,
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.statusText}`);
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'API call failed';
      setApiError(errorMessage);
      throw err;
    } finally {
      setApiLoading(false);
    }
  }, [executeRecaptcha]);

  return {
    callApiWithRecaptcha,
    isLoading: apiLoading,
    error: apiError,
    clearError: () => setApiError(null),
    recaptchaLoading,
    recaptchaError,
    isLoaded,
    lastTokenTime
  };
};

// 🚀 Get base URL for API calls
export const getApiBaseUrl = (): string => {
  return buildApiUrl('');
};

// Direct API functions with reCAPTCHA support and retry mechanism
export const startFullProcessing = async (videoId: string, executeRecaptcha: (action: string) => Promise<string>): Promise<any> => {
  return retryWithDelay(async () => {
    const recaptchaToken = await executeRecaptcha('video_processing');
    console.log('✅ Fresh reCAPTCHA token obtained for start full processing');
    
    // Updated: Call process-full-video endpoint with payment verification
    const response = await apiCallWithRecaptcha('/api/process-full-video', {
      method: 'POST',
      body: JSON.stringify({ 
        video_id: videoId,
        require_payment: true // Enable payment verification
      }),
    }, recaptchaToken);
    
    return response.json();
  }, 3, 500);
};

export const getS3PresignedUrls = async (uploadData: any, executeRecaptcha: (action: string) => Promise<string>): Promise<any> => {
  return retryWithDelay(async () => {
    const recaptchaToken = await executeRecaptcha('file_upload');
    console.log('✅ Fresh reCAPTCHA token obtained for S3 presigned URLs');
    
    const response = await apiCallWithRecaptcha('/api/s3/presigned-urls', {
      method: 'POST',
      body: JSON.stringify(uploadData),
    }, recaptchaToken);
    
    if (!response.ok) {
      // Try to parse error response for detailed message
      let errorMessage = `Failed to get presigned URLs: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
          alert(errorMessage);

        }
        if (errorData.job_in_progress && errorData.job_details) {
          errorMessage = `${errorData.error}\n\nJob in progress: ${errorData.job_details.type} (${errorData.job_details.status})\nVideo ID: ${errorData.job_details.videoId}`;
          // Show browser alert for job in progress error
        }
      } catch (e) {
        // If parsing fails, use default message
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  }, 3, 500);
};

export const startTranslation = async (translationData: any, executeRecaptcha: (action: string) => Promise<string>): Promise<any> => {
  return retryWithDelay(async () => {
    const recaptchaToken = await executeRecaptcha('translation');
    console.log('✅ Fresh reCAPTCHA token obtained for translation start');
    
    const response = await apiCallWithRecaptcha('/api/translate/start', {
      method: 'POST',
      body: JSON.stringify(translationData),
    }, recaptchaToken);
    
    return response.json();
  }, 3, 500);
};

export const translateSrt = async (srtUrl: string, targetLanguage: string, sourceLanguage: string = 'auto', videoId: string | undefined, executeRecaptcha: (action: string) => Promise<string>): Promise<{ translated_srt_url: string; translated_vtt_url?: string }> => {
  return retryWithDelay(async () => {
    console.log('🌐 translateSrt - Starting translation:', {
      srtUrl,
      targetLanguage,
      sourceLanguage
    });

    const recaptchaToken = await executeRecaptcha('translation');
    console.log('✅ Fresh reCAPTCHA token obtained for SRT translation');
    
    const response = await apiCallWithRecaptcha('https://b5wnyvepomkadu55blus4q47pq0nehih.lambda-url.eu-central-1.on.aws', {
      method: 'POST',
      body: JSON.stringify({
        srt_url: srtUrl,
        target_language: targetLanguage,
        source_language: sourceLanguage,
        video_id: videoId
      })
    }, recaptchaToken);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Translation API error:', response.status, errorText);
      throw new Error(`Translation failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ translateSrt - Translation successful:', result);
    
    return result;
  }, 3, 500);
};

// 🚀 NEW: Translate VTT function
export const translateVtt = async (vttUrl: string, targetLanguage: string, sourceLanguage: string = 'auto', executeRecaptcha: (action: string) => Promise<string>, videoId?: string): Promise<{ translated_vtt_url: string }> => {
  return retryWithDelay(async () => {
    console.log('🌐 translateVtt - Starting VTT translation:', {
      vttUrl,
      targetLanguage,
      sourceLanguage
    });

    const recaptchaToken = await executeRecaptcha('translation');
    console.log('✅ Fresh reCAPTCHA token obtained for VTT translation');
    
    const response = await apiCallWithRecaptcha('https://xyz2db21w8.execute-api.eu-central-1.amazonaws.com/sub/translate-vtt', {
      method: 'POST',
      body: JSON.stringify({
        vtt_url: vttUrl,
        target_language: targetLanguage,
        source_language: sourceLanguage,
        video_id: videoId
      })
    }, recaptchaToken);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ VTT Translation API error:', response.status, errorText);
      throw new Error(`VTT Translation failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ translateVtt - VTT Translation successful:', result);
    
    return result;
  }, 3, 500);
};

// Payment API functions
export const createPaymentOrder = async (orderData: any, executeRecaptcha: (action: string) => Promise<string>): Promise<any> => {
  const recaptchaToken = await executeRecaptcha('payment');
  console.log('✅ Fresh reCAPTCHA token obtained for payment order');
  
  const response = await apiCallWithRecaptcha('/api/payment/create-order', {
    method: 'POST',
    body: JSON.stringify(orderData),
  }, recaptchaToken);
  
  return response.json();
};

export const verifyPayment = async (orderId: string): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/payment/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ order_id: orderId }),
  });
  return response.json();
};

export const capturePayment = async (captureData: any): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/payment/capture`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(captureData),
  });
  return response.json();
};

// Video progress API functions
export const getVideoProgress = async (videoId: string): Promise<any> => {
  const response = await fetch(`${getCFWorkerUrl()}/api/job/status?video_id=${videoId}`);
  return response.json();
};

export const getProgress = async (videoId: string, jobId?: string): Promise<any> => {
  const params = new URLSearchParams({ video_id: videoId });
  if (jobId) {
    params.append('job_id', jobId);
  }
  const response = await fetch(`${getCFWorkerUrl()}/api/job/status?${params.toString()}`);
  return response.json();
};

// Download API functions
export const getDownloadLinks = async (videoId: string, type: string): Promise<any> => {
  const params = new URLSearchParams({ video_id: videoId, type });
  
  const response = await fetch(`${getCFWorkerUrl()}/api/download/links?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.json();
};

// Utility function to check if API is accessible
export const checkAPIHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${getCFWorkerUrl()}/health`);
    return response.ok;
  } catch (error) {
    console.error('❌ API Health Check Failed:', error);
    return false;
  }
};
